import { DestroyRef, effect, inject, Injectable, untracked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthStore } from '../../core/auth/auth-store';
import { getQueryParam } from '../../core/initial-search-params';
import { debugLog } from '../../helpers/debugLog';
import type { FinishReadingRequest, ReadingEventSource } from '../../models/reading-stats';
import { MetadataService } from '../metadata/metadata';
import { ReaderApi } from '../reader-api/reader-api';
import { ReaderNavigatorService } from '../readium/navigator/reader-navigator';

/**
 * Reports reading sessions to the stats service, mirroring NR1's StatisticsService: a session is
 * opened on load and its close time is pushed on every page turn; a jump (TOC, slider, search,
 * bookmark...) closes it and opens a new one, and so does reading on after idling on one page
 * longer than MAX_TIME_PER_PAGE.
 */
@Injectable({
  providedIn: 'root',
})
export class ReadingStatsService {
  private static readonly MAX_TIME_PER_PAGE = 300_000;
  private static readonly SESSION_UPDATE_DELAY = 1500;
  private static readonly MINUTES_PER_HOUR = 60;
  private static readonly STATISTICS_URL = '/nubereader/statistics';
  private static readonly OTK_EVENT_SOURCE: ReadingEventSource = 'OdiloTK';
  private static readonly BOOK_CLUB_EVENT_SOURCE: ReadingEventSource = 'BookClub_APP';

  private readonly readerApi = inject(ReaderApi);
  private readonly navigatorService = inject(ReaderNavigatorService);
  private readonly metadataService = inject(MetadataService);
  private readonly authStore = inject(AuthStore);
  private readonly route = inject(ActivatedRoute);

  private isStarted = false;
  /** Starting a session failed (e.g. stats disabled for this token): stop reporting. */
  private isUnavailable = false;
  private readingId: string | undefined;
  private pendingStart: Promise<void> | undefined;
  /** Closed by the per-page time limit (or by leaving the page): the next move opens a new one. */
  private isSessionClosed = false;
  private startProgress: number | undefined;
  private lastProgress: number | undefined;
  private lastPositionAt = Date.now();
  private seenJumpCount = 0;
  private hasInitialPosition = false;
  /** Progress the pending jump left from: where the session it closes actually ended. */
  private jumpOrigin: number | undefined;
  private pageTimeout: ReturnType<typeof setTimeout> | undefined;
  private updateTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      const { resourceLoaded } = this.navigatorService;
      if (this.isStarted || !this.isEnabled()) return;
      if (!resourceLoaded.hasValue() || !resourceLoaded.value()) return;
      this.isStarted = true;
      void this.startSession();
    });

    effect(() => {
      const locator = this.navigatorService.actualLocator();
      const jumpCount = this.navigatorService.jumpCount();
      if (!locator) return;
      untracked(() => {
        this.onPositionChange(jumpCount);
      });
    });

    const onPageHide = (): void => {
      this.finishOnPageHide();
    };
    window.addEventListener('pagehide', onPageHide);
    inject(DestroyRef).onDestroy(() => {
      window.removeEventListener('pagehide', onPageHide);
      clearTimeout(this.updateTimeout);
      clearTimeout(this.pageTimeout);
    });
  }

  private isEnabled(): boolean {
    return this.metadataService.metadata() !== undefined && !this.metadataService.isPreviewMode();
  }

  private onPositionChange(jumpCount: number): void {
    if (!this.isEnabled() || this.isUnavailable) return;

    const isJump = jumpCount !== this.seenJumpCount;
    this.seenJumpCount = jumpCount;
    const previousProgress = this.lastProgress;
    this.lastProgress = this.navigatorService.exactProgress();
    this.startProgress ??= this.lastProgress;
    this.lastPositionAt = Date.now();
    this.armPageTimeout();

    // The first position is where the book opened, not a move by the reader.
    if (!this.hasInitialPosition) {
      this.hasInitialPosition = true;
      return;
    }

    // Readium can report several positions for one move; a jump among them makes it a jump.
    if (isJump) this.jumpOrigin ??= previousProgress;
    clearTimeout(this.updateTimeout);
    this.updateTimeout = setTimeout(() => {
      void this.updateSession();
    }, ReadingStatsService.SESSION_UPDATE_DELAY);
  }

  private armPageTimeout(): void {
    clearTimeout(this.pageTimeout);
    this.pageTimeout = setTimeout(() => {
      if (this.isSessionClosed) return;
      void this.finishSession();
      this.isSessionClosed = true;
    }, ReadingStatsService.MAX_TIME_PER_PAGE);
  }

  private async updateSession(): Promise<void> {
    const { jumpOrigin } = this;
    this.jumpOrigin = undefined;
    // A move made while the session is still being opened must not open a second one.
    await this.pendingStart;
    if (this.isUnavailable || this.readingId === undefined) return;

    if (jumpOrigin === undefined && !this.isSessionClosed) {
      // As NR1 does, a session the server no longer accepts is replaced by a new one.
      if (await this.finishSession()) return;
    } else if (!this.isSessionClosed) {
      // The jump is not reading: the session ends where the reader left from.
      await this.finishSession(jumpOrigin);
    }

    this.startProgress = this.navigatorService.exactProgress();
    await this.startSession();
  }

  private async startSession(): Promise<void> {
    this.isSessionClosed = false;
    this.readingId = undefined;
    this.pendingStart = this.requestSession();
    await this.pendingStart;
  }

  private async requestSession(): Promise<void> {
    try {
      const { id } = await this.readerApi.startReading({
        timezone: ReadingStatsService.getTimezone(),
        club: this.getParam('c'),
      });
      this.readingId = id;
    } catch (error) {
      this.isUnavailable = true;
      debugLog('Reading stats: cannot start a reading session', error);
    }
  }

  private async finishSession(progress?: number): Promise<boolean> {
    const { readingId } = this;
    if (readingId === undefined) return false;
    try {
      await this.readerApi.finishReading(readingId, this.buildFinishRequest(progress));
      return true;
    } catch (error) {
      debugLog('Reading stats: cannot update the reading session', error);
      return false;
    }
  }

  /** The tab is going away: HttpClient requests would be cancelled, a keepalive fetch is not. */
  private finishOnPageHide(): void {
    clearTimeout(this.updateTimeout);
    clearTimeout(this.pageTimeout);
    const { readingId, jumpOrigin } = this;
    this.jumpOrigin = undefined;
    if (readingId === undefined || this.isSessionClosed || this.isUnavailable) return;
    this.isSessionClosed = true;

    void fetch(`${ReadingStatsService.STATISTICS_URL}/${readingId}`, {
      method: 'PUT',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'token': this.authStore.token() },
      body: JSON.stringify(this.buildFinishRequest(jumpOrigin)),
    }).catch((error: unknown) => {
      debugLog('Reading stats: cannot close the reading session', error);
    });
  }

  private buildFinishRequest(
    progress = this.navigatorService.exactProgress(),
  ): FinishReadingRequest {
    const isBookClubApp = this.getParam('bc') !== null && this.getParam('otk') === null;
    return {
      readingPercent: progress - (this.startProgress ?? progress),
      ebookReadingPercentage: Math.round(progress),
      eventSource: isBookClubApp
        ? ReadingStatsService.BOOK_CLUB_EVENT_SOURCE
        : ReadingStatsService.OTK_EVENT_SOURCE,
      club: this.getParam('c'),
      sessionReadingTimeMs: Date.now() - this.lastPositionAt,
    };
  }

  private getParam(key: string): string | null {
    return getQueryParam(this.route.snapshot, key);
  }

  /** UTC offset in hours as NR1 sends it: `+2`, `-5`, `0`. */
  private static getTimezone(): string {
    const hours = -new Date().getTimezoneOffset() / ReadingStatsService.MINUTES_PER_HOUR;
    return hours > 0 ? `+${hours}` : String(hours);
  }
}
